import { authFetch } from "@/lib/api-client";
import type { CurrencyCode, EnabledOpportunityTypes, NorthStarKpi } from "@nexus/contracts";

// ─── Types ──────────────────────────────────────────────────────────

export interface TenantSettingsData {
  defaultBrand: string | null;
  defaultIndustry: string | null;
  /** BQ partition key (`rt.client` / overview `userName`). Resolved server-side. */
  customerPartition: string | null;
  currency: CurrencyCode;
  defaultNorthStar: NorthStarKpi | null;
  dashboardCardOrder: number[];
  dashboardDisabledCards: number[];
  /** Omitted by an older API means every type can still be created. */
  enabledOpportunityTypes?: EnabledOpportunityTypes;
}

export interface CompetitorSettingsRow {
  industry: string;
  mainCompetitor: string | null;
  industryCompetitors: string | null;
}

export interface UpdateTenantSettingsParams {
  defaultBrand?: string | null;
  defaultIndustry?: string | null;
  currency?: CurrencyCode;
}

export interface UpsertCompetitorSettingsParams {
  mainCompetitor?: string | null;
  industryCompetitors?: string | null;
}

// ─── Read ────────────────────────────────────────────────────────────

export async function fetchTenantSettings(): Promise<TenantSettingsData> {
  const { data } = await authFetch<TenantSettingsData>("/tenant-settings");
  return {
    ...data,
    dashboardCardOrder: data.dashboardCardOrder ?? [],
    dashboardDisabledCards: data.dashboardDisabledCards ?? [],
  };
}

export async function fetchCompetitorSettings(): Promise<CompetitorSettingsRow[]> {
  const { data } = await authFetch<CompetitorSettingsRow[]>("/tenant-settings/competitors");
  return data;
}

// ─── Mutations ──────────────────────────────────────────────────────

export async function updateTenantSettings(params: UpdateTenantSettingsParams): Promise<void> {
  await authFetch("/tenant-settings", { method: "PUT", body: JSON.stringify(params) });
}

export async function upsertCompetitorSettings(
  industry: string,
  params: UpsertCompetitorSettingsParams,
): Promise<void> {
  await authFetch(
    `/tenant-settings/competitors/${encodeURIComponent(industry)}`,
    { method: "PUT", body: JSON.stringify(params) },
  );
}
